import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, Tooltip } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import LayersIcon from '@material-ui/icons/Layers'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import {
  useGetAiModelProviders,
  useInsertAiModelProvider,
  useUpdateAiModelProvider,
  useDeleteAiModelProvider
} from '../../../../../api/denaliAssistant'
import { formatRelativeTime } from '../../Analysis/SmartEndpoints/formatRelativeTime'
import { useListStyles } from './useListStyles'
import { PROVIDER_FIELDS } from './fieldSchemas'
import { useToast } from './ToastContext'
import RecordDrawer from './RecordDrawer'
import ConfirmDeleteDialog from './ConfirmDeleteDialog'

function ProvidersTab ({ onDrilldownToModels }) {
  const classes = useListStyles()
  const { showToast, showError } = useToast()
  const { data, isLoading } = useGetAiModelProviders()
  const insertMutation = useInsertAiModelProvider()
  const updateMutation = useUpdateAiModelProvider()
  const deleteMutation = useDeleteAiModelProvider()

  const [searchText, setSearchText] = useState('')
  const [drawer, setDrawer] = useState({ open: false, record: null })
  const [confirm, setConfirm] = useState(null)

  const tableData = useMemo(() => {
    const providers = data?.providers || []
    return providers.map(p => ({
      ...p,
      lastModified: p.modifiedDate || p.createdDate || null,
      lastModifiedBy: p.modifiedBy || p.createdBy || null
    }))
  }, [data])

  /* eslint-disable react/prop-types */
  const columns = useMemo(() => [
    {
      id: 'providerId',
      accessor: 'providerId',
      Header: 'ID',
      Cell: ({ value }) => <span className={classes.mono}>{value}</span>
    },
    { id: 'name', accessor: 'name', Header: 'Name' },
    { id: 'ordinal', accessor: 'ordinal', Header: 'Ordinal' },
    {
      id: 'lastModified',
      accessor: 'lastModified',
      Header: 'Last Modified',
      Cell: ({ row }) => {
        const when = row.original.lastModified
        const who = row.original.lastModifiedBy
        if (!when) return <span className={classes.timestampText}>—</span>
        return (
          <Tooltip title={`${new Date(when).toLocaleString()}${who ? ` by ${who}` : ''}`} arrow>
            <span className={classes.timestampText}>
              {formatRelativeTime(when)}{who ? ` · ${who}` : ''}
            </span>
          </Tooltip>
        )
      }
    },
    {
      id: 'actions',
      Header: '',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '2px' }}>
          {onDrilldownToModels && (
            <Tooltip title='View models for this provider' arrow>
              <IconButton
                size='small'
                className={classes.actionBtn}
                onClick={() => onDrilldownToModels(row.original.providerId)}
              >
                <LayersIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Edit provider' arrow>
            <IconButton
              size='small'
              className={classes.actionBtn}
              onClick={() => setDrawer({ open: true, record: row.original })}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Delete provider' arrow>
            <IconButton
              size='small'
              className={classes.dangerBtn}
              onClick={() => setConfirm({ providerId: row.original.providerId })}
            >
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </div>
      )
    }
  ], [classes, onDrilldownToModels])
  /* eslint-enable react/prop-types */

  const defaultSort = useMemo(() => [{ id: 'ordinal', desc: false }], [])

  const handleDelete = useCallback(async () => {
    if (!confirm) return
    try {
      await deleteMutation.mutateAsync({ providerId: confirm.providerId })
      showToast('Provider deleted')
    } catch (err) {
      showError(err, 'Delete failed')
    } finally {
      setConfirm(null)
    }
  }, [confirm, deleteMutation, showToast, showError])

  const isMutating = insertMutation.isLoading || updateMutation.isLoading
  const openNew = () => setDrawer({ open: true, record: null })

  return (
    <>
      <div className={classes.newBtnWrap}>
        <SydButton size='sm' variant='primary' icon='add' onClick={openNew}>New Provider</SydButton>
      </div>

      {isLoading ? (
        <Loading />
      ) : tableData.length === 0 ? (
        <div className={classes.emptyState}>
          <div className={classes.emptyStateMessage}>
            No AI providers yet. Create one to get started.
          </div>
          <SydButton size='sm' variant='primary' icon='add' onClick={openNew}>New Provider</SydButton>
        </div>
      ) : (
        <>
          <div className={classes.toolbar}>
            <span className={classes.recordCount}>
              {tableData.length} {tableData.length === 1 ? 'provider' : 'providers'}
            </span>
            <input
              className={classes.searchInput}
              placeholder='Search providers…'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className={classes.tableWrap}>
            <OperationalTable
              variant='blank'
              itemName='AI Providers'
              columns={columns}
              data={tableData}
              defaultSort={defaultSort}
              searchText={searchText}
            />
          </div>
        </>
      )}

      <RecordDrawer
        open={drawer.open}
        onClose={() => setDrawer({ open: false, record: null })}
        title={drawer.record ? drawer.record.name : 'New Provider'}
        entityLabel='Provider'
        fields={PROVIDER_FIELDS}
        record={drawer.record}
        isMutating={isMutating}
        onInsert={(payload) => insertMutation.mutateAsync(payload)}
        onUpdate={(payload) => updateMutation.mutateAsync({ ...payload, providerId: drawer.record.providerId })}
      />

      <ConfirmDeleteDialog
        open={!!confirm}
        entityLabel='Provider'
        recordId={confirm?.providerId}
        isMutating={deleteMutation.isLoading}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />
    </>
  )
}

ProvidersTab.propTypes = {
  onDrilldownToModels: PropTypes.func
}

ProvidersTab.defaultProps = {
  onDrilldownToModels: undefined
}

export default ProvidersTab
